/**
 * Opella DAM demo — mock data.
 *
 * Upload sample videos + thumbnails directly to S3:
 *   aws s3 cp ./my-video.mp4 s3://carrie-epics/demos/opella/videos/<filename>.mp4
 *   aws s3 cp ./my-thumb.jpg s3://carrie-epics/demos/opella/thumbnails/<filename>.jpg
 *
 * In local dev you may drop matching files into
 *   frontends/carrie/public/demos/opella/videos/
 *   frontends/carrie/public/demos/opella/thumbnails/
 * (those paths are .gitignored — never commit mp4/jpg binaries).
 *
 * Filenames below mirror the real source files shared by Opella so that
 * dropping the originals into the local public dir renders them unchanged.
 */

export interface OpellaVideo {
  id: string;
  title: string;
  brand: string;
  markets: string[];
  duration: string;
  thumbnailSrc: string;
  videoSrc: string;
  description?: string;
}

export const MARKETS: string[] = ["Germany", "Mexico", "Poland"];

const videoBase = "/demos/opella/videos";
const thumbBase = "/demos/opella/thumbnails";

// Encode the embedded space in the "WORKING BELLY" filename so the <video>
// src resolves correctly without renaming the source file.
const workingBellyFile =
  "IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_WORKING%20BELLY_9x16_V6.mp4";
const workingBellyThumb =
  "IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_WORKING%20BELLY_9x16_V6.jpg";

export const MOCK_VIDEOS: OpellaVideo[] = [
  {
    id: "v-de-meta-reel-1-compliant",
    title: "DE Meta Reel 1 — Compliant cut",
    brand: "Opella",
    markets: ["Germany"],
    duration: "0:14",
    thumbnailSrc: `${thumbBase}/DE_meta_reel_1_compliant.jpg`,
    videoSrc: `${videoBase}/DE_meta_reel_1_compliant.mp4`,
    description: "Reference compliant edit for Meta placement.",
  },
  {
    id: "v-de-meta-reel-1-non-compliant",
    title: "DE Meta Reel 1 — Non-compliant draft",
    brand: "Opella",
    markets: ["Germany"],
    duration: "0:14",
    thumbnailSrc: `${thumbBase}/DE_meta_reel_1_non_compliant.jpg`,
    videoSrc: `${videoBase}/DE_meta_reel_1_non_compliant.mp4`,
    description: "Draft with pending compliance review items.",
  },
  {
    id: "v-mx-meta-reel-1",
    title: "MX Meta Reel 1",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_1.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_1.mp4`,
    description: "Mexico Meta reel, cut 1.",
  },
  {
    id: "v-mx-meta-reel-2",
    title: "MX Meta Reel 2",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:06",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_2.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_2.mp4`,
    description: "Mexico Meta reel, cut 2.",
  },
  {
    id: "v-mx-meta-reel-3",
    title: "MX Meta Reel 3",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:07",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_3.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_3.mp4`,
    description: "Mexico Meta reel, cut 3.",
  },
  {
    id: "v-mx-meta-reel-4",
    title: "MX Meta Reel 4",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:15",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_4.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_4.mp4`,
    description: "Mexico Meta reel, cut 4.",
  },
  {
    id: "v-mx-meta-reel-5",
    title: "MX Meta Reel 5",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:15",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_5.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_5.mp4`,
    description: "Mexico Meta reel, cut 5.",
  },
  {
    id: "v-mx-meta-reel-6",
    title: "MX Meta Reel 6",
    brand: "Opella",
    markets: ["Mexico"],
    duration: "0:20",
    thumbnailSrc: `${thumbBase}/MX_meta_reel_6.jpg`,
    videoSrc: `${videoBase}/MX_meta_reel_6.mp4`,
    description: "Mexico Meta reel, cut 6.",
  },
  {
    id: "v-pl-buscopan-we-love-bellies",
    title: "Buscopan — We Love Bellies (PL, Mill vs Gen Z)",
    brand: "Buscopan",
    markets: ["Poland"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/BR112553_SAF2507_BUSCOPAN_NO_SPA_WE_LOVE_BELLIES_ADAPT_PL_MILL_VS_GENZ_9x16_10s_DMix_v6.jpg`,
    videoSrc: `${videoBase}/BR112553_SAF2507_BUSCOPAN_NO_SPA_WE_LOVE_BELLIES_ADAPT_PL_MILL_VS_GENZ_9x16_10s_DMix_v6.mp4`,
    description: "Polish adaptation of the No-Spa / Buscopan bellies campaign.",
  },
  {
    id: "v-pl-buscopan-bellies-stories-period",
    title: "Buscopan — Bellies Stories: Period",
    brand: "Buscopan",
    markets: ["Poland"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_PERIOD_9x16_V8.jpg`,
    videoSrc: `${videoBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_PERIOD_9x16_V8.mp4`,
    description: "Bellies Stories series — period cramps scenario.",
  },
  {
    id: "v-pl-buscopan-bellies-stories-pool",
    title: "Buscopan — Bellies Stories: Pool",
    brand: "Buscopan",
    markets: ["Poland"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_POOL_9x16_V6.jpg`,
    videoSrc: `${videoBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_POOL_9x16_V6.mp4`,
    description: "Bellies Stories series — pool-day scenario.",
  },
  {
    id: "v-pl-buscopan-bellies-stories-working-belly",
    title: "Buscopan — Bellies Stories: Working Belly",
    brand: "Buscopan",
    markets: ["Poland"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/${workingBellyThumb}`,
    videoSrc: `${videoBase}/${workingBellyFile}`,
    description: "Bellies Stories series — working-day cramps scenario.",
  },
  {
    id: "v-pl-buscopan-bellies-stories-yoga",
    title: "Buscopan — Bellies Stories: Yoga",
    brand: "Buscopan",
    markets: ["Poland"],
    duration: "0:10",
    thumbnailSrc: `${thumbBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_YOGA_9x16_V6.jpg`,
    videoSrc: `${videoBase}/IN101205_SAF2502_BUSCOPAN_BELLIES_STORIES_YOGA_9x16_V6.mp4`,
    description: "Bellies Stories series — yoga scenario.",
  },
];
